/**
 * 节点详情气泡组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import type { GraphNode } from '../../store/graphStore'

interface NodeTooltipProps {
  node: GraphNode | null
  position: { x: number; y: number }
  visible: boolean
}

// 实体类型中文映射
const TYPE_LABELS: Record<string, string> = {
  User: '用户',
  Person: '人物',
  Name: '姓名',
  Family: '家人',
  Friend: '朋友',
  City: '城市',
  CurrentCity: '现居城市',
  Hobby: '爱好',
  Occupation: '职业',
  Company: '公司',
  Food: '食物',
  FavoriteFood: '喜欢的食物',
  Pet: '宠物',
  Event: '事件',
  Other: '其他',
}

export default function NodeTooltip({ node, position, visible }: NodeTooltipProps) {
  if (!visible || !node) return null
  
  const confidence = Math.round((node.confidence ?? 0.8) * 100)
  const typeLabel = TYPE_LABELS[node.type] || node.type
  
  // 计算位置，确保不超出屏幕
  const tooltipStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x + 15,
    top: position.y - 10,
    zIndex: 100,
    pointerEvents: 'none',
    transform: 'translateY(-50%)',
  }
  
  return (
    <div style={tooltipStyle}>
      <div className="rounded-xl shadow-lg p-3 min-w-[180px] animate-fade-in bg-card border border-border">
        {/* 标题 */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg">🧠</span>
          <span className="font-semibold text-foreground">
            {node.name}
          </span>
        </div>
        
        <div className="h-px mb-2 bg-border" />
        
        {/* 属性列表 */}
        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">类型</span>
            <span className="text-foreground">{typeLabel}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">置信度</span>
            <div className="flex items-center gap-2">
              <div className="w-16 h-1.5 rounded-full overflow-hidden bg-muted">
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ 
                    width: `${confidence}%`,
                    background: confidence >= 80 
                      ? '#34C759' 
                      : confidence >= 50 
                        ? '#FF9500' 
                        : '#FF3B30',
                  }}
                />
              </div>
              <span className="text-foreground">{confidence}%</span>
            </div>
          </div>
          
          <div className="flex justify-between">
            <span className="text-muted-foreground">提及次数</span>
            <span className="text-foreground">{(node as any).mention_count || 1}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-muted-foreground">关系数</span>
            <span className="text-foreground">{node.relationCount}</span>
          </div>
        </div>
        
        {/* 来源文本（如果有） */}
        {(node as GraphNode & { source_text?: string }).source_text && (
          <>
            <div className="h-px my-2 bg-border" />
            <div className="text-xs text-muted-foreground">
              <span>来源: </span>
              <span className="italic">
                "{(node as GraphNode & { source_text?: string }).source_text!.slice(0, 30)}
                {(node as GraphNode & { source_text?: string }).source_text!.length > 30 ? '...' : ''}"
              </span>
            </div>
          </>
        )}
        
        {/* 创建时间 */}
        {node.created_at && (
          <div className="text-xs mt-1 text-muted-foreground">
            创建于: {new Date(node.created_at).toLocaleDateString('zh-CN')}
          </div>
        )}
      </div>
    </div>
  )
}

